import { purchaseActualVarianceRatio, roundPurchaseUsd } from './purchaseActualAmount';

/** After a PO is issued, staff may change the amount by this much without new gates. */
export const PURCHASE_APPROVED_AMOUNT_EDIT_LIMIT = 0.1;

export type IssuedPurchaseAmountEditKind = 'unchanged' | 'within_tolerance' | 'needs_approval';

export function approvedPurchaseAmountBaseline(row: {
  approvedAmount?: number | null;
  amount: number;
}): number {
  if (typeof row.approvedAmount === 'number' && Number.isFinite(row.approvedAmount) && row.approvedAmount > 0) {
    return roundPurchaseUsd(row.approvedAmount);
  }
  return roundPurchaseUsd(Number(row.amount) || 0);
}

export function purchaseApprovedAmountWithinTolerance(baseline: number, nextAmount: number): boolean {
  if (!Number.isFinite(nextAmount) || nextAmount <= 0) return false;
  return purchaseActualVarianceRatio(baseline, nextAmount) <= PURCHASE_APPROVED_AMOUNT_EDIT_LIMIT + 1e-12;
}

export function issuedPurchaseAmountEditKind(
  baseline: number,
  nextAmount: number,
): IssuedPurchaseAmountEditKind {
  if (!Number.isFinite(nextAmount) || nextAmount <= 0) return 'needs_approval';
  const next = roundPurchaseUsd(nextAmount);
  const prior = roundPurchaseUsd(baseline);
  if (Math.abs(next - prior) <= 0.005) return 'unchanged';
  return purchaseApprovedAmountWithinTolerance(prior, next) ? 'within_tolerance' : 'needs_approval';
}

/**
 * CRM #75: Adjust-amount Update may save when the dollar total is unchanged
 * if the store/vendor groups changed (backend reallocates PO numbers).
 */
export function canSubmitIssuedPurchaseRevise(
  amountEditKind: IssuedPurchaseAmountEditKind,
  storesChanged: boolean,
): boolean {
  return amountEditKind !== 'unchanged' || storesChanged;
}

export function formatIssuedPurchaseAmountHint(
  baseline: number,
  nextAmount: number,
  opts?: { storesChanged?: boolean },
): string {
  if (!Number.isFinite(nextAmount) || nextAmount <= 0) return 'Enter an amount greater than zero.';
  const kind = issuedPurchaseAmountEditKind(baseline, nextAmount);
  const ratio = purchaseActualVarianceRatio(baseline, nextAmount);
  const storesChanged = !!opts?.storesChanged;
  if (kind === 'unchanged') {
    if (storesChanged) {
      return 'Same as the approved amount. Store/vendor change will issue fresh PO number(s).';
    }
    return 'Same as the approved amount. The issued PO stays as-is.';
  }
  const pct = (ratio * 100).toFixed(1);
  const direction = nextAmount >= baseline ? 'over' : 'under';
  if (kind === 'within_tolerance') {
    const storeNote = storesChanged ? ' Store change issues fresh PO number(s).' : '';
    return `${pct}% ${direction} the approved amount (within 10%). The PO stays issued.${storeNote}`;
  }
  return `${pct}% ${direction} the approved amount — this goes back through approval and the PO number is withdrawn.`;
}
